import {UserSessionData} from "../../app/userSessionData";
import { RequestResult } from "./requestResult";
import JSONbig from "json-bigint";

const JSONbigNative = JSONbig({ useNativeBigInt: true });

export type HTTPMethod = "GET" | "POST" | "PUT" | "DELETE";

export interface IHttpService {
    requestPostAsync(api: string, json: string, additionalHeaders?: any): Promise<Response>

    requestPutAsync(api: string, json: string, additionalHeaders?: any): Promise<Response>

    multipartRequestAsync(api: string, data: Map<string, any>, method: string, files?: {file:File,name:string}[]): Promise<Response>

    requestGetAsync(api: string): Promise<Response>

    requestDeleteAsync(api: string): Promise<Response>

    adminFetch(api: string, method?: HTTPMethod, data?: object, additionalHeaders?: any): Promise<RequestResult>
}

export class HttpService implements IHttpService {
    private static instance: IHttpService;
    private static bot_instance: IHttpService;
    private static bs_instance: IHttpService;

    private apiType: APIType = APIType.API;

    get url(): string {
        switch (this.apiType) {
            case APIType.API:
                return process.env.REACT_APP_API_URL ?? "";
            case APIType.Bot:
                return process.env.REACT_APP_BOT_API_URL ?? "";
            case APIType.BackgroundService:
                return process.env.REACT_APP_BS_API_URL ?? "";
        }
    }

    private constructor(useAPIType: APIType) {
        this.apiType = useAPIType;
    }

    private async safeFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
        let response: Response;
        response = await fetch(input, init);
        if (response && response.status === 401) {
            this.logOff();
        }
        return response;
    }

    public static getInstance(): IHttpService {
        if (!HttpService.instance) {
            HttpService.instance = new HttpService(APIType.API);
        }

        return HttpService.instance;
    }

    public static getBotInstance(): IHttpService {
        if (!HttpService.bot_instance) {
            HttpService.bot_instance = new HttpService(APIType.Bot);
        }

        return HttpService.bot_instance;
    }

    public static getBSInstance(): IHttpService {
        if (!HttpService.bs_instance) {
            HttpService.bs_instance = new HttpService(APIType.BackgroundService);
        }

        return HttpService.bs_instance;
    }

    async requestPostAsync(api: string, json: string, additionalHeaders?: any): Promise<Response> {
        additionalHeaders = additionalHeaders ?? {}
        additionalHeaders = {
            ...additionalHeaders, ...{
                Accept: 'application/json',
                'content-type': 'application/json',
            }
        }
        const token = UserSessionData.getInstance().getToken()
        if (token) {
            additionalHeaders = {
                ...additionalHeaders, ...{
                    Authorization: `Bearer ${token}`,
                }
            }
        }
        const response = await this.safeFetch(`${this.url}/${api}`,
            {
                method: 'POST',
                headers: additionalHeaders,
                body: json,
            });
        return response;
    }

    async requestPutAsync(api: string, json: string, additionalHeaders?: any): Promise<Response> {
        additionalHeaders = additionalHeaders ?? {}
        additionalHeaders = {
            ...additionalHeaders, ...{
                Accept: 'application/json',
                'content-type': 'application/json',
            }
        }
        const token = UserSessionData.getInstance().getToken()
        if (token) {
            additionalHeaders = {
                ...additionalHeaders, ...{
                    Authorization: `Bearer ${token}`,
                }
            }
        }
        const response = await this.safeFetch(`${this.url}/${api}`,
            {
                method: 'PUT',
                headers: additionalHeaders,
                body: json,
            });
        return response;
    }

    async requestDeleteAsync(api: string): Promise<Response> {
        const token = UserSessionData.getInstance().getToken()
        let header = {
            Accept: 'application/json',
            'content-type': 'application/json',
        }
        if (token) {
            header = {
                ...header, ...{
                    Authorization: `Bearer ${token}`,
                }
            }
        }
        const response = await this.safeFetch(`${this.url}/${api}`,
            {
                method: 'DELETE',
                headers: header
            });
        return response;
    }

    async requestGetAsync(api: string): Promise<Response> {
        const token = UserSessionData.getInstance().getToken()
        let header = {
            Accept: 'application/json',
            'content-type': 'application/json',
        }
        if (token) {
            header = {
                ...header, ...{
                    Authorization: `Bearer ${token}`,
                }
            }
        }
        const response = await this.safeFetch(`${this.url}/${api}`,
            {
                method: 'GET',
                headers: header,
            });
        return response;
    }

    async adminFetch(api: string, method: HTTPMethod = "GET", data?: object, additionalHeaders?: any): Promise<RequestResult> {
        let headers = additionalHeaders ?? {};
        headers = {
            ...headers, ...{
                Accept: 'application/json',
                'content-type': 'application/json',
            }
        }
        const token = UserSessionData.getInstance().getToken()
        if (token) {
            headers = {
                ...headers, ...{
                    Authorization: `Bearer ${token}`,
                }
            }
        }

        const response = await this.safeFetch(`${this.url}/admin/${api}`,
        {
            method: method,
            headers: headers,
            body: !!data ? JSONbig.stringify(data) : undefined,
        });

        if (response.status === 204) {
            return new RequestResult(true);
        }

        const text = await response.text(); // Получаем сырой текст ответа
        const body = JSONbigNative.parse(text);
        // Дополнительная проверка на код ошибки авторизации в теле ответа
        const errorCode = (body && body.error && body.error.code) ? body.error.code : undefined;
        if (errorCode === 401) {
            this.logOff();
        }
        if (response.status >= 200 && response.status < 300) {
            return new RequestResult(true, body);
        }
        return new RequestResult(false, body);
    }

    async multipartRequestAsync(api: string, data: Map<string, any>, method: string, files?: {file:File,name:string}[]): Promise<Response> {
        const token = UserSessionData.getInstance().getToken()
        let header = {
            Accept: 'application/json',
        }
        if (token) {
            header = {
                ...header, ...{
                    Authorization: `Bearer ${token}`,
                }
            }
        }
        const formData = new FormData()

        if(files) {
            for (let i = 0; i < files.length; i++) {
                formData.append(files[i].name, files[i].file, files[i].file.name)
            }
        }

        data.forEach((value, key, map) => {
            if (Array.isArray(value)) {
                value.forEach(v => formData.append(key, v));
            } else {
                formData.append(key, value);
            }
        });
        const response = await this.safeFetch(`${this.url}/${api}`,
            {
                method: method,
                headers: header,
                body: formData,
            });
        return response;
    }

    logOff() {
        if(UserSessionData.getInstance().getToken()) {
            UserSessionData.getInstance().clearData();
            window.location.reload();
        }
    }
}

export enum APIType {
    API = 0,
    Bot = 1,
    BackgroundService = 2,
}
